export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public details?: Array<{ path: string; message: string }>,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Origin the API is served from.
 *
 * Empty (the default) means same-origin, which is what the Vite dev proxy and a
 * single-host deployment both want. Set `VITE_API_URL` at build time when the
 * API lives on its own hostname — e.g. the frontend on `example.com` and the
 * API on `api.example.com`. It is read at build time, not run time, so it has
 * to be present for `npm run build`, not just for the running server.
 */
const API_BASE = (import.meta.env.VITE_API_URL ?? '').replace(/\/$/, '');

/** Absolute URL for an API path, for `fetch` and for plain browser navigations. */
export function apiUrl(path: string): string {
  return `${API_BASE}${path}`;
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const isForm = body instanceof FormData;
  const startedAt = performance.now();
  let response: Response;
  try {
    response = await fetch(apiUrl(path), {
      method,
      credentials: 'include',
      headers: isForm || body === undefined ? undefined : { 'Content-Type': 'application/json' },
      body: isForm ? body : body === undefined ? undefined : JSON.stringify(body),
    });
  } catch (err) {
    console.error(
      `[api] ${method} ${path} failed before a response arrived (${Math.round(performance.now() - startedAt)}ms)`,
      err,
    );
    throw err;
  }

  const durationMs = Math.round(performance.now() - startedAt);

  if (response.status === 204) {
    console.debug(`[api] ${method} ${path} -> 204 (${durationMs}ms)`);
    return undefined as T;
  }

  const text = await response.text();
  const payload = text ? safeParse(text) : null;

  if (!response.ok) {
    const message =
      (payload as { error?: string } | null)?.error ?? `Request failed (${response.status})`;
    console.error(`[api] ${method} ${path} -> ${response.status} (${durationMs}ms): ${message}`, {
      body,
      payload,
    });
    throw new ApiError(response.status, message, (payload as { details?: never } | null)?.details);
  }

  console.debug(`[api] ${method} ${path} -> ${response.status} (${durationMs}ms)`);

  // A 200 that is not JSON means the request never reached the API. The usual
  // cause is a static host answering with index.html because of the SPA
  // fallback, which happens when VITE_API_URL was not set for a build whose API
  // is on another hostname. Say so, rather than handing HTML to a caller that
  // is about to read properties off it.
  if (typeof payload !== 'object' || payload === null) {
    throw new ApiError(
      response.status,
      `Expected JSON from ${apiUrl(path)} but got ${
        response.headers.get('content-type') ?? 'an unknown content type'
      }. If the API is on a different hostname, rebuild the frontend with VITE_API_URL set to it.`,
    );
  }

  return payload as T;
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  del: <T>(path: string) => request<T>('DELETE', path),
};

// ---------------------------------------------------------------------------
// Shared API types
// ---------------------------------------------------------------------------
export type Channel = 'email' | 'linkedin_dm' | 'linkedin_post_engage';
export type ProspectStatus = 'new' | 'contacted' | 'replied' | 'won' | 'lost' | 'archived';
/** How much the address on file can be trusted. Only 'verified' is ever sent to; 'unknown' means no address. */
export type EmailStatus = 'verified' | 'inferred' | 'unknown';
export type Intent = 'warm' | 'cold' | 'objection' | 'interested' | 'waiting' | 'unclear';

export interface User {
  id: string;
  email: string;
  full_name: string;
  role: 'admin' | 'member';
  is_active?: boolean;
  owned_prospects?: number;
}

export interface Campaign {
  id: string;
  name: string;
  description: string;
  context: string;
  status: 'active' | 'paused' | 'closed';
  /** ISO weekdays this campaign may email on, 1 = Monday .. 7 = Sunday. */
  send_days: number[];
  /** Recipient-local send window, minutes since midnight. */
  send_window_start: number;
  send_window_end: number;
  default_timezone: string | null;
  /** Max sends/day across all prospects in this campaign. null = unlimited. */
  daily_send_cap: number | null;
  /** Key of the ICP in profile.yml this campaign is for. null = the default ICP. */
  icp_key: string | null;
  created_at: string;
  prospect_count?: number;
  new_count?: number;
  contacted_count?: number;
  replied_count?: number;
  won_count?: number;
  lost_count?: number;
}

export interface Persona {
  id: string;
  campaign_id: string;
  name: string;
  description: string;
  job_titles: string[];
  industry_focus: string[];
  email_prompt: string;
  linkedin_dm_prompt: string;
  linkedin_post_idea_prompt: string;
  prospect_count?: number;
}

export interface SequenceStep {
  id?: string;
  step_order?: number;
  day_offset: number;
  channel: Channel;
  message_type: string;
  skip_if_replied: boolean;
  instructions: string;
}

export interface Prospect {
  id: string;
  first_name: string;
  last_name: string;
  email: string | null;
  email_status: EmailStatus;
  company_name: string;
  job_title: string;
  company_industry: string;
  deal_value: string | null;
  linkedin_url: string | null;
  source: string;
  custom_fields: Record<string, unknown>;
  notes: string;
  campaign_id: string | null;
  persona_id: string | null;
  status: ProspectStatus;
  timezone: string | null;
  timezone_source: 'ai_inferred' | 'csv_import' | 'manual' | null;
  timezone_confidence: 'high' | 'medium' | 'low' | null;
  created_at: string;
  campaign_name?: string | null;
  persona_name?: string | null;
  last_contacted_at?: string | null;
  touch_count?: number;
  reply_count?: number;
  company_id?: string | null;
  lifecycle_stage: LifecycleStage;
  tags: string[];
  last_touch_at: string | null;
  summary: string;
  summary_next_step: string;
  next_step_date: string | null;
  /** While in the future, the sequence is held for an out-of-office auto-reply. */
  sequence_paused_until: string | null;
  sequence_paused_reason: string | null;
  summary_updated_at: string | null;
}

export type TodayItemKind = 'next_step' | 'reply' | 'sequence_step' | 'stale_deal' | 'renewal';

export interface TodayItem {
  kind: TodayItemKind;
  ref: string;
  prospect_id: string;
  prospect_name: string;
  company_name: string | null;
  title: string;
  detail: string;
  due_at: string | null;
}

export type LifecycleStage = 'lead' | 'engaged' | 'qualified' | 'customer' | 'churned' | 'disqualified';

export type TimelineKind =
  | 'email_out'
  | 'email_in'
  | 'linkedin_dm'
  | 'linkedin_touch'
  | 'note'
  | 'call'
  | 'meeting'
  | 'signal'
  | 'deal_stage'
  | 'lifecycle_change'
  | 'system';

export interface TimelineEvent {
  id: string;
  prospect_id: string | null;
  company_id: string | null;
  kind: TimelineKind;
  occurred_at: string;
  actor: 'user' | 'contact' | 'system' | 'claude';
  title: string;
  body: string;
  source: string;
  source_ref: string | null;
  metadata: Record<string, unknown>;
  created_by: string | null;
  created_at: string;
  prospect_name?: string | null;
  company_name?: string | null;
}

export interface Company {
  id: string;
  name: string;
  domain: string | null;
  industry: string;
  employee_count: number | null;
  linkedin_url: string | null;
  lifecycle_stage: LifecycleStage;
  owner_id: string | null;
  tags: string[];
  source: string;
  external_ids: Record<string, unknown>;
  description: string;
  summary: string;
  summary_next_step: string;
  next_step_date: string | null;
  summary_updated_at: string | null;
  last_touch_at: string | null;
  created_at: string;
  prospect_count?: number;
}

export interface Activity {
  id: string;
  prospect_id: string;
  campaign_id: string | null;
  channel: Channel;
  message_type: string;
  status: 'drafted' | 'approved' | 'scheduled' | 'sent' | 'failed' | 'skipped';
  subject: string | null;
  claude_draft: string;
  your_edits: string | null;
  final_message: string | null;
  error_message: string | null;
  sent_at: string | null;
  scheduled_for: string | null;
  /** Null until an admin approves. Sending and scheduling both require it. */
  approved_by: string | null;
  approved_at: string | null;
  approved_by_name?: string | null;
  email_opened: boolean;
  email_open_count: number;
  email_clicked: boolean;
  email_click_count: number;
  /** Null: nothing to ground in (no company research or recent signal existed). True/false: whether the draft used what it had. */
  personalization_grounded: boolean | null;
  personalization_note: string;
  created_at: string;
  prospect_name?: string;
  company_name?: string;
  prospect_email?: string | null;
  prospect_email_status?: EmailStatus | null;
  linkedin_url?: string | null;
  campaign_name?: string | null;
}

export interface ClaudeAnalysis {
  intent: Intent;
  sentiment_score: number;
  key_objections: string[];
  pain_points_mentioned: string[];
  budget_signals: string[];
  timeline_signals: string[];
  recommended_next_step: string;
  suggested_action: 'follow_up' | 'escalate' | 'close_won' | 'close_lost' | 'manual_review';
  summary: string;
  confidence_score: number;
}

export interface Reply {
  id: string;
  prospect_id: string;
  campaign_id: string | null;
  channel: 'email' | 'linkedin_dm';
  message_body: string;
  received_at: string;
  claude_analysis: ClaudeAnalysis | null;
  intent: Intent | null;
  sentiment_score: string | null;
  confidence_score: string | null;
  your_action: string | null;
  action_taken_at: string | null;
  prospect_name?: string;
  company_name?: string;
  campaign_name?: string | null;
}

// ---------------------------------------------------------------------------
// Ask (phase 6 of docs/CRM_ROADMAP.md) — POST /api/ask
// ---------------------------------------------------------------------------
export interface AskEvent {
  id: string;
  occurred_at: string;
  kind: string;
  title: string;
  body: string;
  prospect_name: string | null;
  company_name: string | null;
}

export interface AskCitation {
  event_id: string;
  why: string;
  event: AskEvent | null;
}

export interface AskResult {
  answer: string;
  citations: AskCitation[];
  events_considered: number;
}

export interface Outcome {
  id: string;
  prospect_id: string;
  outcome: 'won' | 'lost' | 'no_reply' | 'unqualified';
  reason: string;
  deal_value: string | null;
  closed_at: string;
  suggested_by_claude: boolean;
  prospect_name?: string;
  company_name?: string;
}

export type DealStage = 'lead' | 'qualified' | 'quoted' | 'negotiating' | 'won' | 'lost';

/**
 * Board order, matching STAGES in server/src/services/deals.ts and the CHECK
 * constraint in 007_deals.sql. `GET /api/deals/stages` serves the same list
 * with labels for other API consumers; the frontend uses this constant instead
 * so the stage set is pinned at compile time by DealStage rather than arriving
 * asynchronously as data.
 */
export const DEAL_STAGES: DealStage[] = [
  'lead',
  'qualified',
  'quoted',
  'negotiating',
  'won',
  'lost',
];

export const CLOSED_DEAL_STAGES: DealStage[] = ['won', 'lost'];
export const OPEN_DEAL_STAGES: DealStage[] = DEAL_STAGES.filter(
  (stage) => !CLOSED_DEAL_STAGES.includes(stage),
);

export function isClosedDealStage(stage: DealStage): boolean {
  return CLOSED_DEAL_STAGES.includes(stage);
}

export interface Deal {
  id: string;
  prospect_id: string;
  campaign_id: string | null;
  name: string;
  stage: DealStage;
  stage_entered_at: string;
  /** NUMERIC arrives as a string from pg; parse before arithmetic. */
  value: string | null;
  currency: string;
  probability_pct: number | null;
  expected_close_date: string | null;
  owner_id: string | null;
  closed_at: string | null;
  close_reason: string;
  created_at: string;
  updated_at: string;
  // Joined on list and prospect-detail responses.
  prospect_name?: string;
  company_name?: string;
  prospect_email?: string | null;
  campaign_name?: string | null;
  owner_name?: string | null;
  /** Open deals only: when anything last happened on the prospect's or company's timeline (see STALE_DEAL_DAYS). */
  quiet_since?: string | null;
  // Retainer terms (all optional). Dates arrive as ISO strings for a calendar day; show them with dateOnly().
  recurring_amount?: string | null;
  recurring_period?: RecurringPeriod | null;
  included_volume?: number | null;
  included_volume_unit?: VolumeUnit | null;
  term_start?: string | null;
  term_end?: string | null;
  renewal_date?: string | null;
}

export const RECURRING_PERIODS = ['month', 'quarter', 'year'] as const;
export type RecurringPeriod = (typeof RECURRING_PERIODS)[number];
export const VOLUME_UNITS = ['words', 'hours'] as const;
export type VolumeUnit = (typeof VOLUME_UNITS)[number];

export interface PipelineStage {
  stage: DealStage;
  label: string;
  open: boolean;
  count: number;
  value: number;
  median_age_days: number | null;
}

export interface StageVelocity {
  stage: DealStage;
  label: string;
  transitions: number;
  median_days: number | null;
  progression_rate: number | null;
}

export interface DealHistoryEntry {
  id: string;
  deal_id: string;
  from_stage: DealStage | null;
  to_stage: DealStage;
  note: string;
  changed_by: string | null;
  changed_by_name: string | null;
  changed_at: string;
}

/** Kept in step with DEAL_CONTACT_ROLES in server/src/services/dealContacts.ts (GET /api/deals/contact-roles). */
export const DEAL_CONTACT_ROLES = [
  { key: 'champion', label: 'Champion' },
  { key: 'economic_buyer', label: 'Economic buyer' },
  { key: 'technical_evaluator', label: 'Technical evaluator' },
  { key: 'legal_procurement', label: 'Legal or procurement' },
  { key: 'user', label: 'User' },
  { key: 'other', label: 'Other' },
] as const;
export type DealContactRole = (typeof DEAL_CONTACT_ROLES)[number]['key'];

/** A person attached to a deal with a role, besides the deal's primary contact. */
export interface DealContact {
  id: string;
  deal_id: string;
  prospect_id: string;
  role: DealContactRole;
  created_at: string;
  prospect_name: string;
  job_title: string;
  company_name: string;
  email: string | null;
  is_primary: boolean;
}

/** A role seen from the person's side. */
export interface ProspectDealRole {
  id: string;
  deal_id: string;
  prospect_id: string;
  role: DealContactRole;
  deal_name: string;
  stage: DealStage;
  value: string | null;
  currency: string;
}

export type InteractionType = 'note' | 'call' | 'meeting';
export type CallOutcome = 'connected' | 'voicemail' | 'no_answer' | 'wrong_number';

export const INTERACTION_TYPES: InteractionType[] = ['note', 'call', 'meeting'];
export const CALL_OUTCOMES: CallOutcome[] = [
  'connected',
  'voicemail',
  'no_answer',
  'wrong_number',
];

/** A call, meeting or note logged by hand — distinct from `Activity`, which is outbound mail. */
export interface Interaction {
  id: string;
  prospect_id: string;
  deal_id: string | null;
  type: InteractionType;
  subject: string;
  body: string;
  /** When it happened, which is not when it was typed up — order timelines by this. */
  occurred_at: string;
  duration_minutes: number | null;
  call_outcome: CallOutcome | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  // Joined.
  created_by_name?: string | null;
  deal_name?: string | null;
  prospect_name?: string;
  company_name?: string;
}

export interface PlannedTouch {
  step_order: number;
  day_offset: number;
  channel: Channel;
  message_type: string;
  due_at: string | null;
  status: 'sent' | 'drafted' | 'pending' | 'skipped';
}

export interface Funnel {
  prospects: number;
  contacted: number;
  replied: number;
  warm: number;
  won: number;
  lost: number;
  reply_rate: number;
  warm_rate: number;
  win_rate: number;
}

export interface Engagement {
  sent: number;
  opened: number;
  clicked: number;
  open_rate: number;
  click_rate: number;
}

export interface TimelinePoint {
  day: string;
  sent: number;
  replies: number;
  won: number;
}

export interface RoiRow {
  campaign_id: string | null;
  campaign_name: string;
  prospects: number;
  sent: number;
  replies: number;
  won: number;
  lost: number;
  won_value: number;
  deals_created: number;
  deals_won: number;
  open_deals: number;
  open_deal_value: number;
  pipeline_value: number;
  value_per_prospect: number;
}

export interface Dashboard {
  funnel: Funnel;
  engagement: Engagement;
  timeline: TimelinePoint[];
  hot_list: Array<{
    id: string;
    prospect_id: string;
    prospect_name: string;
    company_name: string;
    intent: Intent | null;
    sentiment_score: string | null;
    received_at: string;
    message_body: string;
    recommended_next_step: string | null;
  }>;
  recent_activity: Activity[];
  pending_drafts: number;
}

export interface LinkedInPost {
  id: string;
  campaign_id: string | null;
  persona_id: string | null;
  content: string;
  status: 'idea' | 'scheduled' | 'published';
  post_url: string | null;
  posted_at: string | null;
  scheduled_for: string | null;
  likes: number;
  comments: number;
  profile_views: number;
  campaign_name?: string | null;
  persona_name?: string | null;
  engagement_count?: number;
}

export interface PostIdea {
  hook: string;
  angle: string;
  draft: string;
}

/** One person parsed and scored from pasted LinkedIn / Sales Navigator text. */
export interface QualifiedCandidate {
  first_name: string;
  last_name: string;
  job_title: string;
  company_name: string;
  location: string | null;
  linkedin_url: string | null;
  profile_snippet: string;
  fit_score: number;
  verdict: 'strong_fit' | 'possible_fit' | 'poor_fit';
  fit_reasons: string[];
  matched_keywords: string[];
  already_in_crm: boolean;
}

export type IntegrationProvider =
  | 'anthropic'
  | 'gmail'
  | 'linkedin'
  | 'apollo'
  | 'theirstack'
  | 'trigify';

export interface IntegrationStatus {
  provider: IntegrationProvider;
  connected: boolean;
  metadata: Record<string, unknown>;
  updated_at: string | null;
}

export interface GmailSuggestedContact {
  id: string;
  email: string;
  display_name: string;
  domain: string | null;
  first_seen_at: string;
  last_seen_at: string;
  thread_count: number;
  status: 'pending' | 'accepted' | 'ignored';
  accepted_prospect_id: string | null;
}

export interface GmailIgnoredDomain {
  domain: string;
  created_at: string;
}

export interface ContactAlias {
  id: string;
  prospect_id: string;
  email: string;
  created_at: string;
}

export interface GmailTimelineSyncResult {
  messagesChecked: number;
  eventsAdded: number;
  suggestionsAdded: number;
  errors: string[];
}

export type GmailBackfillStatus = 'idle' | 'running' | 'completed' | 'error';

export interface GmailBackfillState {
  status: GmailBackfillStatus;
  since: string | null;
  pageToken: string | null;
  messagesChecked: number;
  eventsAdded: number;
  suggestionsAdded: number;
  errors: string[];
  startedAt: string | null;
  updatedAt: string | null;
}

export type IntentProviderName = 'apollo' | 'theirstack' | 'trigify';

/** One editable config field, as described by the server. */
export interface IntentConfigField {
  name: string;
  label: string;
  type: 'lines' | 'number' | 'tiers';
  hint: string;
  placeholder?: string;
  min?: number;
  max?: number;
}

/** A group of keywords sharing one signal strength. */
export interface KeywordTier {
  label: string;
  strength: number;
  keywords: string[];
}

/**
 * A signal provider and its stored state. The server returns an entry for every
 * known provider, so one that has never been configured still renders with its
 * defaults.
 */
export interface IntentSource {
  provider: IntentProviderName;
  label: string;
  description: string;
  fields: IntentConfigField[];
  defaults: Record<string, unknown>;
  /** Config keys that must be non-empty before the provider returns anything. */
  requires: string[];
  configured: boolean;
  enabled: boolean;
  config: Record<string, unknown>;
  last_synced_at: string | null;
}

export interface IntentSyncResult {
  synced: number;
  inserted: number;
  duplicates: number;
  scored: number;
  skipped: string[];
  errors: string[];
}

/** The monthly AI budget (GET /api/ai/budget, backlog #51). */
export interface AIBudgetStatus {
  /** Calendar month the figures are for, YYYY-MM (UTC). */
  month: string;
  /** Null when no budget is set: nothing is capped. */
  budget_usd: number | null;
  spent_usd: number;
  fraction: number | null;
  level: 'none' | 'ok' | 'warning' | 'exceeded';
  /** Present once optional background AI calls have been refused this month. */
  stop: {
    first_at: string;
    last_at: string;
    embeddings_skipped: number;
    summary_ticks_skipped: number;
  } | null;
}
